/**
 * Algorithm Configuration Form
 * Create and edit algorithm configurations
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../trading/card'
import { Input, Textarea, Select } from '../trading/input'
import { Badge } from '../trading/badge'
import { Tabs, TabsPanel, TabPanel } from '../trading/tabs'
import { Save, Play, Square, Settings, TrendingUp, Shield, Clock } from 'lucide-react'

interface AlgorithmFormProps {
  algorithm?: any
  onSave: (algorithm: any) => void
  onCancel: () => void
}

const STRATEGIES = [
  { value: 'trend-following', label: 'Trend Following' },
  { value: 'mean-reversion', label: 'Mean Reversion' },
  { value: 'momentum', label: 'Momentum' },
  { value: 'breakout', label: 'Breakout' },
  { value: 'arbitrage', label: 'Arbitrage' },
  { value: 'market-making', label: 'Market Making' },
  { value: 'ml-based', label: 'ML-Based' },
]

const SYMBOLS = [
  { value: 'EUR/USD', label: 'EUR/USD' },
  { value: 'GBP/USD', label: 'GBP/USD' },
  { value: 'USD/JPY', label: 'USD/JPY' },
  { value: 'BTC/USD', label: 'BTC/USD' },
  { value: 'ETH/USD', label: 'ETH/USD' },
  { value: 'XAU/USD', label: 'XAU/USD (Gold)' },
]

const TIMEFRAMES = [
  { value: '1m', label: '1 Minute' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
]

export function AlgorithmForm({ algorithm, onSave, onCancel }: AlgorithmFormProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState(
    algorithm || {
      name: '',
      description: '',
      strategy: '',
      symbol: '',
      timeframe: '',
      status: 'inactive',
      parameters: {
        entryConditions: [],
        exitConditions: [],
        positionSize: 1,
        maxPositions: 3,
        stopLoss: 50,
        takeProfit: 100,
        trailingStop: 0,
      },
      riskManagement: {
        maxDrawdown: 20,
        dailyLossLimit: 500,
        maxRiskPerTrade: 2,
        emergencyStopEnabled: true,
      },
      schedule: {
        enabled: false,
        startTime: '00:00',
        endTime: '23:59',
        weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      },
    }
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  const updateField = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  const updateParameter = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      parameters: { ...prev.parameters, [field]: value },
    }))
  }

  const updateRiskManagement = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      riskManagement: { ...prev.riskManagement, [field]: value },
    }))
  }

  const updateSchedule = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: value },
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{algorithm ? 'Edit Algorithm' : 'Create New Algorithm'}</CardTitle>
            {algorithm && <Badge variant={algorithm.status === 'active' ? 'success' : 'default'}>{algorithm.status}</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            tabs={[
              { id: 'general', label: 'General', icon: <Settings className="h-4 w-4" /> },
              { id: 'parameters', label: 'Parameters', icon: <TrendingUp className="h-4 w-4" /> },
              { id: 'risk', label: 'Risk Management', icon: <Shield className="h-4 w-4" /> },
              { id: 'schedule', label: 'Schedule', icon: <Clock className="h-4 w-4" /> },
            ]}
            defaultTab="general"
            onChange={setActiveTab}
            variant="pills"
          />

          <TabsPanel>
            <TabPanel value="general" activeTab={activeTab}>
              <div className="space-y-4 pt-6">
                <Input
                  label="Algorithm Name"
                  placeholder="My Trading Algorithm"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                />
                <Textarea
                  label="Description"
                  placeholder="Describe your algorithm strategy..."
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                />
                <Select
                  label="Strategy Type"
                  options={STRATEGIES}
                  value={formData.strategy}
                  onChange={(e) => updateField('strategy', e.target.value)}
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Symbol"
                    options={SYMBOLS}
                    value={formData.symbol}
                    onChange={(e) => updateField('symbol', e.target.value)}
                    required
                  />
                  <Select
                    label="Timeframe"
                    options={TIMEFRAMES}
                    value={formData.timeframe}
                    onChange={(e) => updateField('timeframe', e.target.value)}
                    required
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel value="parameters" activeTab={activeTab}>
              <div className="space-y-4 pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Position Size (Lots)"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.parameters.positionSize}
                    onChange={(e) => updateParameter('positionSize', parseFloat(e.target.value))}
                    helperText="Size per trade"
                  />
                  <Input
                    label="Max Positions"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.parameters.maxPositions}
                    onChange={(e) => updateParameter('maxPositions', parseInt(e.target.value))}
                    helperText="Maximum concurrent positions"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Stop Loss (Pips)"
                    type="number"
                    min="0"
                    value={formData.parameters.stopLoss}
                    onChange={(e) => updateParameter('stopLoss', parseInt(e.target.value))}
                  />
                  <Input
                    label="Take Profit (Pips)"
                    type="number"
                    min="0"
                    value={formData.parameters.takeProfit}
                    onChange={(e) => updateParameter('takeProfit', parseInt(e.target.value))}
                  />
                  <Input
                    label="Trailing Stop (Pips)"
                    type="number"
                    min="0"
                    value={formData.parameters.trailingStop}
                    onChange={(e) => updateParameter('trailingStop', parseFloat(e.target.value))}
                    helperText="0 = disabled"
                  />
                </div>
                <div className="p-4 rounded-lg bg-muted border border-border">
                  <h4 className="text-sm font-medium mb-2">Entry Conditions</h4>
                  <p className="text-xs text-muted-foreground">Define your entry logic rules</p>
                  <Textarea
                    placeholder="e.g., RSI < 30 AND Price < Lower Bollinger Band"
                    className="mt-2"
                    rows={2}
                  />
                </div>
                <div className="p-4 rounded-lg bg-muted border border-border">
                  <h4 className="text-sm font-medium mb-2">Exit Conditions</h4>
                  <p className="text-xs text-muted-foreground">Define your exit logic rules</p>
                  <Textarea
                    placeholder="e.g., RSI > 70 OR Price > Upper Bollinger Band"
                    className="mt-2"
                    rows={2}
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel value="risk" activeTab={activeTab}>
              <div className="space-y-4 pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Max Drawdown (%)"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.riskManagement.maxDrawdown}
                    onChange={(e) => updateRiskManagement('maxDrawdown', parseInt(e.target.value))}
                    helperText="Stop trading if exceeded"
                  />
                  <Input
                    label="Daily Loss Limit ($)"
                    type="number"
                    min="0"
                    value={formData.riskManagement.dailyLossLimit}
                    onChange={(e) => updateRiskManagement('dailyLossLimit', parseFloat(e.target.value))}
                    helperText="Stop if daily loss exceeds"
                  />
                </div>
                <Input
                  label="Max Risk Per Trade (%)"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={formData.riskManagement.maxRiskPerTrade}
                  onChange={(e) => updateRiskManagement('maxRiskPerTrade', parseFloat(e.target.value))}
                  helperText="Maximum portfolio risk per trade"
                />
                <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                  <div>
                    <h4 className="text-sm font-medium">Emergency Stop</h4>
                    <p className="text-xs text-muted-foreground">Automatically stop all trading on critical conditions</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.riskManagement.emergencyStopEnabled}
                      onChange={(e) => updateRiskManagement('emergencyStopEnabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="schedule" activeTab={activeTab}>
              <div className="space-y-4 pt-6">
                <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                  <div>
                    <h4 className="text-sm font-medium">Enable Trading Schedule</h4>
                    <p className="text-xs text-muted-foreground">Only trade during specific hours</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.schedule.enabled}
                      onChange={(e) => updateSchedule('enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {formData.schedule.enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Start Time"
                        type="time"
                        value={formData.schedule.startTime}
                        onChange={(e) => updateSchedule('startTime', e.target.value)}
                      />
                      <Input
                        label="End Time"
                        type="time"
                        value={formData.schedule.endTime}
                        onChange={(e) => updateSchedule('endTime', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Active Days</label>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              const days = formData.schedule.weekdays.includes(day)
                                ? formData.schedule.weekdays.filter((d: string) => d !== day)
                                : [...formData.schedule.weekdays, day]
                              updateSchedule('weekdays', days)
                            }}
                            className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                              formData.schedule.weekdays.includes(day)
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-muted-foreground border-border hover:border-primary'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TabPanel>
          </TabsPanel>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {algorithm && algorithm.status === 'active' && (
              <Button type="button" variant="destructive">
                <Square className="h-4 w-4 mr-2" />
                Stop Trading
              </Button>
            )}
            {algorithm && algorithm.status === 'inactive' && (
              <Button type="button" variant="outline">
                <Play className="h-4 w-4 mr-2" />
                Start Trading
              </Button>
            )}
            <Button type="submit">
              <Save className="h-4 w-4 mr-2" />
              Save Algorithm
            </Button>
          </div>
        </CardFooter>
      </Card>
    </form>
  )
}
